import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { STORY_TIMELINE, COUPLE } from "@/lib/constants";
import Reveal from "@/components/Reveal";
import { getStories } from "@/lib/api";

export default function Story() {
  const [items, setItems] = useState(STORY_TIMELINE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getStories()
      .then((data) => {
        if (mounted && Array.isArray(data) && data.length > 0) setItems(data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="story-page" className="relative pt-32 pb-24 px-5 md:px-10">
      <Reveal className="text-center max-w-3xl mx-auto">
        <p className="text-[0.65rem] uppercase tracking-[0.5em] text-gold mb-4">
          Our Journey
        </p>
        <h1 className="font-serif font-light text-5xl md:text-7xl text-ivory leading-tight">
          The Story of <span className="italic gold-text">#PandA</span>
        </h1>
        <div className="ornament-divider w-72 mx-auto mt-8">
          <Heart size={14} className="text-gold/80 fill-gold/80" />
        </div>
        <p className="mt-6 font-serif italic text-lg md:text-xl text-ivory-muted">
          {loading ? "Gathering our chapters…" : "Chapters of a love story still being written."}
        </p>
      </Reveal>

      <div className="relative max-w-5xl mx-auto mt-20 md:mt-28">
        <div className="timeline-line" />
        <ul className="space-y-16 md:space-y-24">
          {items.map((item, i) => {
            const isLeft = i % 2 === 0;
            return (
              <li
                key={item.id || item.year + i}
                data-testid={`timeline-item-${i}`}
                className="relative md:grid md:grid-cols-2 md:gap-12 items-center"
              >
                <div className="timeline-dot" />
                <Reveal
                  delay={120}
                  className={`pl-12 md:pl-0 ${
                    isLeft ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"
                  }`}
                >
                  <div className="glass-card p-7 md:p-8 group inline-block w-full">
                    <p className="text-[0.6rem] uppercase tracking-[0.4em] text-gold">
                      {item.year}
                    </p>
                    <h3 className="mt-2 font-serif text-3xl md:text-4xl text-ivory">
                      {item.title}
                    </h3>
                    <div
                      className={`mt-4 h-px bg-gradient-to-r from-gold/0 via-gold/60 to-gold/0 ${
                        isLeft ? "md:ml-auto md:w-2/3" : "md:w-2/3"
                      }`}
                    />
                    <p className="mt-4 font-sans text-base text-ivory-muted leading-relaxed whitespace-pre-line">
                      {item.body}
                    </p>
                    <p
                      className={`mt-4 font-serif italic text-sm gold-text ${
                        isLeft ? "md:text-right" : ""
                      }`}
                    >
                      #PandA · Chapter {i + 1}
                    </p>
                  </div>
                </Reveal>

                <Reveal
                  delay={220}
                  className={`hidden md:block ${
                    isLeft ? "md:col-start-2" : "md:row-start-1 md:col-start-1"
                  }`}
                >
                  <div className="relative overflow-hidden rounded-3xl border border-gold/20 shadow-[0_18px_60px_rgba(0,0,0,0.55)] aspect-[4/5] group">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-ink-900/10 to-transparent" />
                    <span className="absolute bottom-4 left-4 right-4 font-serif italic text-gold-light text-lg">
                      {item.title}
                    </span>
                  </div>
                </Reveal>

                <Reveal delay={220} className="md:hidden mt-6 pl-12">
                  <div className="relative overflow-hidden rounded-2xl border border-gold/20 aspect-[16/10]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 to-transparent" />
                  </div>
                </Reveal>
              </li>
            );
          })}
        </ul>
      </div>

      <Reveal className="mt-24 md:mt-28 text-center max-w-2xl mx-auto">
        <div className="ornament-divider w-72 mx-auto mb-8">
          <Heart size={14} className="text-gold/80" />
        </div>
        <p className="font-serif italic text-2xl md:text-3xl text-ivory-muted leading-relaxed">
          “And the rest, dear friends, is the story we’ll write together.”
        </p>
        <p className="mt-6 font-serif italic text-3xl md:text-4xl hashtag-glow">
          #PandA Begins ❤️
        </p>
        <p className="mt-2 text-xs uppercase tracking-[0.4em] text-ivory-dim">
          {COUPLE.weddingDateLabel} · {COUPLE.venue}
        </p>
      </Reveal>
    </div>
  );
}
